import type { FC } from "react";
import { useState } from "react";
import { useIntl } from "@/infra/locale";
import { AppRoutes } from "@/infra/router";
import type { TermsAndConditions } from "@akina/domain";
import { useFetchLegalPage, useUpdateTermsAndConditionsStatus } from "@akina/infra";
import { Dialog, Text, Checkbox, ErrorMessage, TextLink, Button } from "@akina/presentation";

type UpdatedTermsAndPrivacyPolicyAcceptanceModalProps = {
  open: boolean;
  onOpenChange: (prev: boolean) => void;
};

export const UpdatedTermsAndPrivacyPolicyAcceptanceModal: FC<UpdatedTermsAndPrivacyPolicyAcceptanceModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { formatMessage } = useIntl();
  const { data: termsAndConditionsLegalPage } = useFetchLegalPage("terms-and-conditions");
  const { data: privacyPolicyLegalPage } = useFetchLegalPage("privacy-policy-and-data-transparency");

  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [errorPrivacyPolicy, setErrorPrivacyPolicy] = useState(false);

  const updateStatus = useUpdateTermsAndConditionsStatus({
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  const handleAccept = () => {
    if (acceptPrivacyPolicy && privacyPolicyLegalPage && termsAndConditionsLegalPage) {
      const payload: TermsAndConditions = {
        termsAndConditionsAccepted: true,
        dataPrivacyPolicyAccepted: true,
        termsAndConditionsAcceptedTimestamp: new Date().toISOString(),
        dataPrivacyPolicyAcceptedTimestamp: new Date().toISOString(),
        privacyPolicyVersion: privacyPolicyLegalPage.version,
        termsVersion: termsAndConditionsLegalPage.version,
      };
      updateStatus.mutate(payload, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    } else {
      setErrorPrivacyPolicy(true);
    }
  };

  return (
    <Dialog
      withClose={false}
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && !acceptPrivacyPolicy) {
          setErrorPrivacyPolicy(true);
        } else {
          onOpenChange(isOpen);
        }
      }}
      onInteractOutside={event => event.preventDefault()}
      className="no-scrollbar flex flex-col">
      <Text size="3xl" className="pb-10">
        {formatMessage({
          id: "legal.updatedTermsAndPrivacyPolicyAcceptance.title",
        })}
      </Text>
      <div className="flex flex-col gap-6">
        <Text size="base">
          {formatMessage({
            id: "legal.updatedTermsAndPrivacyPolicyAcceptance.description",
          })}
        </Text>
        <div className="mt-2 flex cursor-pointer flex-row">
          <div className="mr-3">
            <Checkbox
              checked={acceptPrivacyPolicy}
              onCheckedChange={() => {
                setAcceptPrivacyPolicy(!acceptPrivacyPolicy);
                setErrorPrivacyPolicy(false);
              }}
            />
          </div>
          <button onClick={() => setAcceptPrivacyPolicy(!acceptPrivacyPolicy)}>
            <div className="flex flex-wrap gap-1">
              <Text color={"text-gray-800/50"} size="base" className="whitespace-nowrap">
                {formatMessage({
                  id: "legal.label",
                })}
              </Text>
              <a
                onClick={e => e.stopPropagation()}
                title={formatMessage({
                  id: "legal.termsOfService",
                })}
                target="_blank"
                href={AppRoutes.TermsAndConditions}
                rel="noreferrer">
                <TextLink size="base" className="whitespace-nowrap">
                  {formatMessage({
                    id: "legal.termsOfService",
                  })}
                </TextLink>
              </a>
              <Text size="base" color={"text-gray-800/50"}>
                {formatMessage({
                  id: "legal.and",
                })}
              </Text>
              <a
                onClick={e => e.stopPropagation()}
                title={formatMessage({
                  id: "legal.privacyPolicy",
                })}
                target="_blank"
                href={AppRoutes.PrivacyPolicyAndDataTransparency}
                rel="noreferrer">
                <TextLink size="base" className="whitespace-nowrap">
                  {formatMessage({
                    id: "legal.privacyPolicy",
                  })}
                </TextLink>
              </a>
            </div>
          </button>
        </div>
        {errorPrivacyPolicy ? (
          <ErrorMessage
            error={formatMessage({
              id: "legal.updatedTermsAndPrivacyPolicyAcceptance.error",
            })}
            size="xs"
            className="mt-0"
          />
        ) : null}
        <Button
          loading={updateStatus.isPending}
          className="ml-auto max-w-fit object-contain text-right"
          onClick={handleAccept}>
          {formatMessage({
            id: "legal.button",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
